import React from 'react';
import { Link } from 'react-router-dom';
import { AppstoreOutlined, LogoutOutlined, ProfileOutlined, UserOutlined, LoginOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Avatar, Dropdown, Button } from 'antd';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';

interface UserMenuProps {
  isMobile?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({ isMobile = false }) => {
  const auth = useAuth();
  const { t } = useTranslation('components');

  const handleLogin = () => {
    auth.signinRedirect();
  };

  const handleLogout = async () => {
    const idToken = auth.user?.id_token;
    const redirectUri = window.location.origin;

    if (idToken) {
      await auth.signoutRedirect({
        post_logout_redirect_uri: redirectUri,
      });
    } else {
      await auth.removeUser();
      window.location.href = '/';
    }
  };

  if (!auth.isAuthenticated || !auth.user) {
    if (isMobile) {
      return (
        <Button type="primary" icon={<LoginOutlined />} onClick={handleLogin} block>
          {t('userMenu.login')}
        </Button>
      );
    }

    return (
      <Button type="primary" icon={<LoginOutlined />} onClick={handleLogin}>
        {t('userMenu.login')}
      </Button>
    );
  }

  const username: string =
    auth.user.profile.name ||
    auth.user.profile.preferred_username ||
    auth.user.profile.email ||
    t('userMenu.userFallback');

  const canAccessAdmin = auth.user.profile.groups?.some((group) =>
    group.startsWith('/official-site/')
  );

  const items: MenuProps['items'] = [
    {
      key: 'username',
      label: t('userMenu.greeting', { username }),
      icon: <UserOutlined />,
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'profile',
      label: <Link to="/profile">{t('userMenu.profile')}</Link>,
      icon: <ProfileOutlined />,
    },
    ...(canAccessAdmin
      ? [
          {
            key: 'admin',
            label: <Link to="/admin">{t('userMenu.admin')}</Link>,
            icon: <AppstoreOutlined />,
          },
        ]
      : []),
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: t('userMenu.logout'),
      icon: <LogoutOutlined />,
      onClick: handleLogout,
      danger: true,
    },
  ];

  if (isMobile) {
    return (
      <div>
        <div style={{ marginBottom: '8px' }}>
          <Avatar style={{ backgroundColor: '#1890ff' }} icon={<UserOutlined />} />
          <span style={{ marginLeft: '8px' }}>{username}</span>
        </div>
        <Link to="/profile" style={{ display: 'block', padding: '8px 0' }}>
          <ProfileOutlined style={{ marginRight: '8px' }} />
          {t('userMenu.profile')}
        </Link>
        {canAccessAdmin && (
          <Link to="/admin" style={{ display: 'block', padding: '8px 0' }}>
            <AppstoreOutlined style={{ marginRight: '8px' }} />
            {t('userMenu.admin')}
          </Link>
        )}
        <a
          onClick={handleLogout}
          style={{ display: 'block', padding: '8px 0', color: '#ff4d4f' }}
        >
          <LogoutOutlined style={{ marginRight: '8px' }} />
          {t('userMenu.logout')}
        </a>
      </div>
    );
  }

  return (
    <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
      <Avatar
        style={{ cursor: 'pointer', backgroundColor: '#1890ff' }}
        icon={<UserOutlined />}
      />
    </Dropdown>
  );
};

export default UserMenu;
